import { ApplicantType } from '../constants/permit.constants';
import { I18nString } from './permit-category.model';
import { PermitFile, PopulatedRef, PopulatedStatus } from './permit.model';

/** A permit type for display: the external description renders as a subtitle. */
export interface PermitTypeDisplay {
    externalDescription?: I18nString;
}

function resolveI18nOrString(value: I18nString | string | undefined, lang: string): string {
    if (!value) return '';
    return typeof value === 'string' ? value : value[lang] || value.fr || value.en || '';
}

/** Subtitle for a permit type: the external description ('' when none). */
export function permitTypeSubtitle(type: PermitTypeDisplay | null | undefined, lang: string): string {
    return resolveI18nOrString(type?.externalDescription, lang);
}

export interface FeeConfig {
    timing: 'upfront' | 'later';
    type?: 'fixed' | 'informational';
    amount?: number; // for 'fixed' fees
    description?: I18nString; // free-text shown to the citizen for 'informational' fees
}

export interface ExternalPaymentConfig {
    enabled: boolean;
    message?: I18nString; // shown to the citizen instead of the payment dialog
}

export interface FormFieldConfig {
    fieldId: string; // unique within the permit type
    label: I18nString;
    // Backend contract property name is `fieldType` (not `type`).
    fieldType: 'text' | 'textarea' | 'number' | 'date' | 'dropdown' | 'checkbox' | 'file' | 'address' | 'url';
    required: boolean;
    presetValue?: string; // static reference value (e.g. url); not citizen-editable
    options?: (string | { value: string; label: string | I18nString })[]; // for dropdown
    min?: number; // for number
    max?: number; // for number
    displayOrder: number;
    section?: string; // optional grouping label
    // Set when the field originates from an external system sync (e.g. `wf:<id>` / `doc:<id>`).
    // Absent on admin-authored fields.
    externalId?: string;
    // Free-text help set by the PSD sync for fields with no dedicated unit (e.g. currency/measurement hints).
    description?: I18nString;
}

/**
 * A group of form fields, synced from the external system (e.g. PSD work-field groups).
 * `fieldIds` lists the member fields in their intended display order (which may differ
 * from each field's global `displayOrder`).
 */
export interface FormSectionConfig {
    name: I18nString;
    fieldIds: string[];
    displayOrder: number;
}

/** A group of fields to render together under an (optional) localized title. */
export interface GroupedFields {
    title: I18nString | null;
    fields: FormFieldConfig[];
}

/**
 * Groups fields by section (ordered by `displayOrder`, then per-section `fieldIds`); unreferenced
 * fields go in a trailing untitled group, in input order (don't re-sort — callers pre-order them).
 * Returns `null` when there are no sections, signalling the caller to use its own single-group fallback.
 */
export function groupFieldsBySections(fields: FormFieldConfig[], formSections: FormSectionConfig[] | undefined): GroupedFields[] | null {
    if (!formSections?.length) {
        return null;
    }
    const byId = new Map(fields.map((field) => [field.fieldId, field]));
    const referenced = new Set<string>();
    const groups: GroupedFields[] = [];

    [...formSections]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .forEach((section) => {
            const sectionFields: FormFieldConfig[] = [];
            for (const fieldId of section.fieldIds || []) {
                const field = byId.get(fieldId);
                if (field) {
                    sectionFields.push(field);
                    referenced.add(fieldId);
                }
            }
            if (sectionFields.length) {
                groups.push({ title: section.name, fields: sectionFields });
            }
        });

    const leftover = fields.filter((field) => !referenced.has(field.fieldId));
    if (leftover.length) {
        groups.push({ title: null, fields: leftover });
    }
    return groups;
}

// PSD's fixed description text for currency fields that have no admin-set unit yet.
export const PSD_CURRENCY_DESCRIPTION: I18nString = { en: 'Amount in $', fr: 'Montant en $' };

/**
 * Best-effort display unit for a 'number' field, in the given language: the admin-set override
 * from `PermitTypeConfig.fieldUnits` (keyed by fieldId), else '$' if PSD flagged it as currency
 * ('$' is language-invariant, unlike the override). `fieldUnits` lives outside `formFields` so the
 * PSD sync never overwrites it.
 */
export function displayUnitFor(field: FormFieldConfig, fieldUnits: Record<string, I18nString> | undefined, lang: string): string | undefined {
    const override = fieldUnits?.[field.fieldId];
    const resolved = override?.[lang] || override?.fr || override?.en;
    if (resolved) return resolved;
    if (field.description?.en === PSD_CURRENCY_DESCRIPTION.en || field.description?.fr === PSD_CURRENCY_DESCRIPTION.fr) {
        return '$';
    }
    return undefined;
}

export interface PredefinedTask {
    title: string;
    assignedTo?: string; // User ObjectId
}

export interface AutoReassignmentRule {
    triggerStatus: string; // status ObjectId
    targetDepartment: string; // department ObjectId
}

export interface PermitTypeSummary {
    _id: string;
    name: I18nString;
    // PSD external description (translated), rendered as a subtitle. See permitTypeSubtitle.
    externalDescription?: I18nString;
    active: boolean;
    feeConfig?: FeeConfig;
    externalPayment?: ExternalPaymentConfig;
    // When true, the form (formFields/formSections) is owned by an external system sync and
    // is read-only in the admin console. Only meaningful on imported types (externalId set).
    connected?: boolean;
    externalId?: string; // present on types imported from an external system
}

export interface PermitTypeConfig extends PermitTypeSummary {
    description?: I18nString;
    numberPrefix: string; // e.g. 'BP' — 2-4 uppercase letters, unique per org
    defaultDepartment: string | PopulatedRef;
    applicantTypes: ApplicantType[];
    validityDurationDays?: number;
    preExpirationNotifyDays?: number;
    formFields: FormFieldConfig[];
    // Groups formFields under named sections (synced from the external system). Absent on non-synced types.
    formSections?: FormSectionConfig[];
    // Admin-set display units keyed by FormFieldConfig.fieldId (e.g. { "wf:2": { fr: "pi²", en: "ft²" } }).
    // Stored outside formFields so it survives the PSD sync's full-array overwrite.
    fieldUnits?: Record<string, I18nString>;
    // Template/resource files synced from the external system (e.g. by-law PDFs, fee schedules).
    files?: PermitFile[];
    statuses: string[] | PopulatedStatus[];
    predefinedTasks: PredefinedTask[];
    autoReassignmentRules: AutoReassignmentRule[];
    displayOrder: number;
}

export interface PermitTypeSearchResult {
    _id: string;
    name: I18nString | string;
    categoryId: string;
    categoryName: I18nString | string;
}

export interface CreatePermitTypePayload {
    name: I18nString;
    description?: I18nString;
    numberPrefix: string;
    defaultDepartment: string; // department ObjectId
    applicantTypes: ApplicantType[]; // at least one required
    feeConfig?: FeeConfig;
    externalPayment?: ExternalPaymentConfig;
    validityDurationDays?: number;
    preExpirationNotifyDays?: number;
    formFields?: FormFieldConfig[];
    fieldUnits?: Record<string, I18nString>;
    statuses?: string[]; // status ObjectIds; defaults applied if omitted
    predefinedTasks?: PredefinedTask[];
    autoReassignmentRules?: AutoReassignmentRule[];
    active?: boolean;
    displayOrder?: number;
}

export type UpdatePermitTypePayload = Partial<CreatePermitTypePayload>;
